import api from "services/apiClient"
import ENDPOINTS from "constants/endpoints"
import { fetchWithValidation } from "helpers/fetchWithValidation"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { z } from "zod"
import { type Notification, NotificationSchema, type NotificationCount, NotificationCountSchema } from "../models/notification"

const NOTIFICATIONS_QUERY_KEY = ["notifications", "list"]
const NOTIFICATIONS_COUNT_QUERY_KEY = ["notifications", "count"]

export const useNotificationsList = (limit = 20) => {
  return useQuery<Notification[], Error>({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, limit],
    queryFn: () =>
      fetchWithValidation(
        `${ENDPOINTS.NOTIFICATION}?limit=${limit}`,
        z.array(NotificationSchema)
      ),
  })
}

export const useNotificationsCount = () => {
  return useQuery<NotificationCount, Error>({
    queryKey: NOTIFICATIONS_COUNT_QUERY_KEY,
    queryFn: () =>
      fetchWithValidation(
        `${ENDPOINTS.NOTIFICATION}/count`,
        NotificationCountSchema
      ),
    refetchInterval: 60000, // Poll count every 60 seconds
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })
}

export const useMarkAsRead = () => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await api.post(`${ENDPOINTS.NOTIFICATION}/read/${id}`)
    },
    onSuccess: () => {
      // Invalidate both lists and count
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    },
  })
}

export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await api.post(`${ENDPOINTS.NOTIFICATION}/read-all`)
    },
    onSuccess: () => {
      // Invalidate both lists and count
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    },
  })
}
